const encoder = new TextEncoder();
const HEX = "0123456789abcdef";

export const PBKDF2_DEFAULT_ITERATIONS = 50_000;

function bytesToHex(bytes: Uint8Array): string {
  let out = "";
  for (const byte of bytes) {
    out += HEX[(byte >>> 4) & 0x0f] ?? "0";
    out += HEX[byte & 0x0f] ?? "0";
  }
  return out;
}

function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0 || /[^0-9a-f]/i.test(hex)) {
    throw new Error("Invalid hex string");
  }
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function bufferSource(bytes: Uint8Array): BufferSource {
  return bytes as unknown as BufferSource;
}

export function secureEqual(a: string, b: string): boolean {
  return constantTimeEqual(encoder.encode(a), encoder.encode(b));
}

function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  const length = Math.max(a.length, b.length);
  let diff = a.length ^ b.length;
  for (let i = 0; i < length; i += 1) {
    diff |= (a[i] ?? 0) ^ (b[i] ?? 0);
  }
  return diff === 0;
}

let pepperCache: { value: string; key: Promise<CryptoKey> } | null = null;

function pepperKey(pepper: string): Promise<CryptoKey> {
  if (pepperCache?.value === pepper) return pepperCache.key;
  const key = crypto.subtle.importKey(
    "raw",
    encoder.encode(pepper),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  pepperCache = { value: pepper, key };
  return key;
}

export async function hmacSha256(key: string, data: string | Uint8Array): Promise<Uint8Array> {
  const signature = await crypto.subtle.sign(
    "HMAC",
    await pepperKey(key),
    typeof data === "string" ? encoder.encode(data) : bufferSource(data),
  );
  return new Uint8Array(signature);
}

export async function hmacSha256Hex(key: string, data: string | Uint8Array): Promise<string> {
  return bytesToHex(await hmacSha256(key, data));
}

export async function sha256Hex(data: string | Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    typeof data === "string" ? encoder.encode(data) : bufferSource(data),
  );
  return bytesToHex(new Uint8Array(digest));
}

export function randomHex(byteLength = 32): string {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength));
  return bytesToHex(bytes);
}

export async function derivePasswordHash(
  password: string,
  pepper: string,
  salt: Uint8Array,
  iterations = PBKDF2_DEFAULT_ITERATIONS,
): Promise<Uint8Array> {
  const passwordBytes = encoder.encode(password);
  const material = pepper
    ? new Uint8Array(
        await crypto.subtle.sign("HMAC", await pepperKey(pepper), bufferSource(passwordBytes)),
      )
    : passwordBytes;
  const key = await crypto.subtle.importKey("raw", bufferSource(material), "PBKDF2", false, [
    "deriveBits",
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt: bufferSource(salt), iterations, hash: "SHA-256" },
    key,
    256,
  );
  return new Uint8Array(bits);
}

export async function hashPassword(
  password: string,
  pepper: string,
  iterations = PBKDF2_DEFAULT_ITERATIONS,
): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const derived = await derivePasswordHash(password, pepper, salt, iterations);
  const prefix = pepper ? "pbkdf2p" : "pbkdf2";
  return `${prefix}$${iterations}$${bytesToHex(salt)}$${bytesToHex(derived)}`;
}

export interface ParsedPasswordHash {
  hasPepper: boolean;
  iterations: number;
  salt: Uint8Array;
  hash: Uint8Array;
}

export function parsePasswordHash(stored: string): ParsedPasswordHash | null {
  const parts = stored.split("$");
  if (parts.length !== 4) return null;
  const [prefix, iterationsRaw, saltHex, hashHex] = parts;
  if (prefix !== "pbkdf2p" && prefix !== "pbkdf2") return null;
  const iterations = Number.parseInt(iterationsRaw ?? "", 10);
  if (!Number.isFinite(iterations) || iterations < 1 || iterations > 2_000_000) return null;
  try {
    return {
      hasPepper: prefix === "pbkdf2p",
      iterations,
      salt: hexToBytes(saltHex ?? ""),
      hash: hexToBytes(hashHex ?? ""),
    };
  } catch {
    return null;
  }
}

export async function verifyPassword(
  stored: string,
  password: string,
  pepper: string,
): Promise<boolean> {
  const parsed = parsePasswordHash(stored);
  if (!parsed) return false;
  const derived = await derivePasswordHash(
    password,
    parsed.hasPepper ? pepper : "",
    parsed.salt,
    parsed.iterations,
  );
  return constantTimeEqual(derived, parsed.hash);
}

export function passwordHashNeedsRehash(
  stored: string,
  pepper: string,
  iterations = PBKDF2_DEFAULT_ITERATIONS,
): boolean {
  const parsed = parsePasswordHash(stored);
  if (!parsed) return true;
  if (parsed.hasPepper !== Boolean(pepper)) return true;
  return parsed.iterations < iterations;
}

/** RFC 9562 UUIDv7, generated locally because D1 has no UUID function. */
export function uuidv7(now = Date.now()): string {
  const bytes = new Uint8Array(16);
  let timestamp = BigInt(now);
  for (let i = 5; i >= 0; i -= 1) {
    bytes[i] = Number(timestamp & 0xffn);
    timestamp >>= 8n;
  }
  crypto.getRandomValues(bytes.subarray(6));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = bytesToHex(bytes);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
